/**
 * Small pieces shared by the SKU, Brand and Platform sales views (pages/Sales*Page.js).
 *
 *   usePageScope(props)        -> { scope, setScope, salesScope }. When the parent passes {scope, setScope}
 *                                 it owns the header and control bar and salesScope is null. Otherwise the
 *                                 page uses useSalesScope() and salesScope is the full hook result, so the
 *                                 page renders <ViewHeader salesScope={salesScope} ... /> itself.
 *   ViewHeader                 -> h1 + period/compare line + SalesControlBar (renders nothing without salesScope).
 *   useSalesOptions(country, enabled) -> { brands, categories } from the catalog (hub options; one small,
 *                                 scope-independent request) for pages whose own endpoint has no option lists.
 *   useScopeLink()             -> (path, patch) => "/sales/skus?brand=Drogo&..." keeping the page's
 *                                 current URL scope ('' / null / 'ALL' remove a key, like setScope).
 *   pctText(v)                 -> "12.5%" | "—"         changePctText(row) -> "+6.2%" | "New" | "—"
 *   previousFromChange(rev, pct) -> previous value implied by a % change (null when not derivable)
 *   CoverBadge, ChangePct, EmptyState, TableSkeleton, CardsSkeleton, MetricToggle
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Skeleton } from '../../ui/skeleton';
import { useSalesScope } from '../useSalesScope';
import { useSalesData } from '../salesApi';
import { SalesControlBar } from '../SalesControlBar';
import { coverTone, indiaToday, number, shiftDay, signedPct } from '../format';

export const usePageScope = (props = {}) => {
  const own = useSalesScope();
  const external = Boolean(props.scope && props.setScope);
  return {
    scope: external ? props.scope : own.scope,
    setScope: external ? props.setScope : own.setScope,
    salesScope: external ? null : own,
  };
};

/** Page heading plus the shared control bar; only used when the page owns its scope (salesScope set). */
export const ViewHeader = ({ salesScope, title, testId, brands, categories, onRefresh, loading, hide, showSearch = false, children }) => {
  if (!salesScope) return null;
  const { periodLabel, compareLabel } = salesScope;
  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" data-testid={testId}>{title}</h1>
          <p className="mt-1 text-sm text-gray-500">{periodLabel} · {String(compareLabel || '').replace(/^vs /, 'compared with ')}</p>
        </div>
      </header>
      <SalesControlBar salesScope={salesScope} brands={brands} categories={categories} onRefresh={onRefresh} loading={loading}
        hide={hide} showSearch={showSearch}>
        {children}
      </SalesControlBar>
    </>
  );
};

/** Catalog brand / category names (hub `options` are catalog-wide, so a fixed one-day query is enough). */
export const useSalesOptions = (country, enabled = true) => {
  const day = shiftDay(indiaToday(), -1);
  const { data } = useSalesData(enabled ? 'hub' : null, { country: country || 'IN', date_from: day, date_to: day }, { dimension: 'brand' });
  return { brands: data?.options?.brands || [], categories: data?.options?.categories || [] };
};

export const useScopeLink = () => {
  const location = useLocation();
  return (path, patch = {}) => {
    const params = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([key, value]) => {
      if (value == null || value === '' || value === 'ALL') params.delete(key);
      else params.set(key, String(value));
    });
    const query = params.toString();
    return `${path}${query ? `?${query}` : ''}`;
  };
};

export const pctText = value => (value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`);

export const changePctText = ({ revenue, previous_revenue: previous, revenue_change: change, is_new: isNew }) => {
  if (isNew || (!previous && revenue > 0)) return 'New';
  if (change == null) return '—';
  return Number(change) === 0 ? '0%' : signedPct(Number(change));
};

/** The previous value implied by `current` and a % change; null when it cannot be worked out. */
export const previousFromChange = (current, pct) => {
  if (pct == null) return current > 0 ? 0 : null; // backend sends null change only when previous was 0
  if (Number(pct) === -100) return null;
  return Number(current) / (1 + Number(pct) / 100);
};

export const COVER_STYLES = {
  danger: 'text-red-800 bg-red-50 ring-red-200',
  warning: 'text-amber-800 bg-amber-50 ring-amber-200',
  success: 'text-green-800 bg-green-50 ring-green-200',
  neutral: 'text-gray-600 bg-gray-100 ring-gray-200',
};

export const CoverBadge = ({ days, stock, className = '' }) => {
  const tone = coverTone(days);
  const text = days == null ? (stock > 0 ? 'No sales' : '—') : `${number(days)} d`;
  const title = days == null
    ? (stock > 0 ? 'Has stock but no sales in this period' : 'Cover unknown')
    : `${number(days)} days of stock at this period's daily sales`;
  return (
    <span title={title} aria-label={title}
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ring-1 ring-inset ${COVER_STYLES[tone]} ${className}`}>
      {text}
    </span>
  );
};

export const ChangePct = ({ row, className = '' }) => {
  const text = changePctText(row);
  const tone = text === 'New' ? 'text-sky-700' : text.startsWith('+') ? 'text-green-700' : text.startsWith('−') ? 'text-red-700' : 'text-gray-500';
  return <span className={`tabular-nums ${tone} ${className}`}>{text}</span>;
};

export const EmptyState = ({ title, children, action, testId }) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center" data-testid={testId}>
    <p className="font-medium text-gray-900">{title}</p>
    {children && <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">{children}</p>}
    {action && <div className="mt-3 flex flex-wrap justify-center gap-2">{action}</div>}
  </div>
);

export const TableSkeleton = ({ rows = 8, label = 'Loading' }) => (
  <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-3" aria-busy="true" aria-label={label}>
    <Skeleton className="h-6 w-full" />
    {Array.from({ length: rows }).map((_, index) => <Skeleton key={index} className="h-10 w-full" />)}
  </div>
);

export const CardsSkeleton = ({ count = 4, className = 'h-28', label = 'Loading' }) => (
  <div className="space-y-2" aria-busy="true" aria-label={label}>
    {Array.from({ length: count }).map((_, index) => <Skeleton key={index} className={`w-full ${className}`} />)}
  </div>
);

export const MetricToggle = ({ value, onChange, options = [['revenue', 'Revenue'], ['units', 'Units']], label = 'Chart metric' }) => (
  <div role="group" aria-label={label} className="inline-flex rounded-lg bg-gray-100 p-0.5">
    {options.map(([key, text]) => (
      <button key={key} type="button" aria-pressed={value === key} onClick={() => onChange(key)}
        className={`h-7 rounded-md px-2.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${value === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
        {text}
      </button>
    ))}
  </div>
);

export const shortDate = day => `${Number(day.slice(8, 10))}/${Number(day.slice(5, 7))}`;

export const hasFilters = scope => Boolean(scope && ((scope.platform && scope.platform !== 'ALL') || (scope.brand && scope.brand !== 'ALL')
  || (scope.category && scope.category !== 'ALL') || scope.sku || scope.search));

export const CLEAR_FILTERS = { platform: '', brand: '', category: '', sku: '', search: '' };
